import { LoaderCircle, Mic, Square, X } from 'lucide-react'
import { useId } from 'react'

import { cn } from './lib/ui'
import type { useVoiceInput } from './useVoiceInput'

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export function VoiceFieldControl({ voice, field, label, value, onChange, disabled = false }: {
  voice: ReturnType<typeof useVoiceInput>
  field: string
  label: string
  value: string
  onChange: (text: string) => void
  disabled?: boolean
}) {
  const privacyId = useId()
  const current = voice.field === field
  const busy = current && voice.busy
  const listening = busy && voice.status === 'listening'
  const recording = busy && voice.status === 'recording'
  const starting = busy && voice.status === 'starting'
  const stopping = busy && voice.status === 'stopping'
  const transcribing = busy && voice.status === 'transcribing'
  const capturing = listening || recording
  const name = label.toLowerCase()
  const verb = voice.engine === 'whisper' ? 'recording' : 'dictation'
  const action = capturing ? `Stop ${name} ${verb}` : starting ? `Cancel ${name} ${verb}`
    : stopping || transcribing ? `Finishing ${name} ${verb}` : `Start ${name} ${verb}`

  return (
    <div role="group" aria-label={`${label} voice input`} className="space-y-1">
      <div className="flex min-h-8 flex-wrap items-center gap-1.5">
        <button
          type="button"
          aria-label={action}
          aria-describedby={privacyId}
          title={action}
          disabled={disabled || (voice.busy && !current) || stopping || transcribing}
          onClick={() => {
            if (capturing) voice.stop()
            else if (starting) voice.cancel()
            else voice.start(field, value, onChange)
          }}
          className={cn('flex size-8 shrink-0 items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:opacity-40',
            busy ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'text-ink-500 hover:bg-brand-50 hover:text-brand-700')}
        >
          {stopping || transcribing ? <LoaderCircle size={16} className="motion-safe:animate-spin" aria-hidden="true" />
            : capturing ? <Square size={14} fill="currentColor" aria-hidden="true" />
              : starting ? <X size={16} aria-hidden="true" /> : <Mic size={16} aria-hidden="true" />}
        </button>
        {busy && (
          <>
            <span role="status" className="text-xs text-ink-500 tabular-nums">
              {recording ? `Recording ${clock(voice.seconds)} · click to stop`
                : listening ? 'Listening…'
                  : starting ? 'Allow microphone access…'
                    : transcribing ? 'Transcribing…' : 'Finishing…'}
            </span>
            {!starting && !transcribing && <button type="button" onClick={voice.cancel}
              aria-label={`Cancel ${name} ${verb}`} title={`Cancel ${verb} and keep current text`}
              className="flex size-8 items-center justify-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700">
              <X size={14} aria-hidden="true" />
            </button>}
          </>
        )}
      </div>
      <span id={privacyId} className="sr-only">
        {voice.engine === 'whisper'
          ? 'Your recording is transcribed on the Taskly server and then discarded. Taskly saves only text.'
          : 'Your browser may send audio to its speech service. Taskly saves only text.'}
      </span>
      {current && voice.error && <p role="alert" className="text-xs text-red-700">{voice.error}</p>}
    </div>
  )
}
